import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Container, Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../assets/errors/nodata.png';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DownloadIcon from '@mui/icons-material/Download';

// ─── Shared holiday / working-day helpers ────────────────────────────────────

const NAMIBIA_HOLIDAYS_RAW = [
  // 2026
  '2026-01-01', // New Year's Day
  '2026-03-21', // Independence Day
  '2026-04-03', // Good Friday
  '2026-04-06', // Easter Monday
  '2026-05-01', // Workers' Day
  '2026-05-04', // Cassinga Day
  '2026-05-14', // Ascension Day
  '2026-05-25', // Africa Day
  '2026-08-26', // Heroes' Day
  '2026-12-10', // Human Rights Day
  '2026-12-25', // Christmas Day
  '2026-12-26', // Day of Goodwill

  // 2027
  '2027-01-01',
  '2027-03-21',
  '2027-04-03',
  '2027-04-06',
  '2027-05-01',
  '2027-05-04',
  '2027-05-14',
  '2027-05-25',
  '2027-08-26',
  '2027-12-10',
  '2027-12-25',
  '2027-12-26',
];

// Shift any Sunday public holiday to the following Monday (as per the Act)
const PUBLIC_HOLIDAYS = new Set(
  NAMIBIA_HOLIDAYS_RAW.map((dateStr) => {
    const d = new Date(dateStr);
    if (d.getDay() === 0) {
      d.setDate(d.getDate() + 1);
    }
    return d.toISOString().split('T')[0];
  })
);

/**
 * Count working days (Mon–Fri, excluding Namibian public holidays)
 * between two ISO date strings (inclusive).
 */
const countWorkingDays = (startStr, endStr) => {
  let count = 0;
  const cursor = new Date(startStr);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(endStr);
  end.setHours(0, 0, 0, 0);

  while (cursor <= end) {
    const day = cursor.getDay();
    const dateStr = cursor.toISOString().split('T')[0];
    if (day !== 0 && day !== 6 && !PUBLIC_HOLIDAYS.has(dateStr)) {
      count++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

/**
 * Maximum working days allowed per leave type.
 * Leave types not listed here have no enforced cap.
 */
const LEAVE_TYPE_LIMITS = {
  Annual:        4,
  Compassionate: 5,
  Sick:          1,
  Maternity:     40,
};

// ─────────────────────────────────────────────────────────────────────────────

const Leave = () => {
  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [leaveCredits, setLeaveCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userID, userRole } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    staff_id: '',
    leave_type: '',
    reason: '',
    startDate: '',
    endDate: '',
    back_in_office: '',
    department: '',
    hr_appr_id: '',
    hr_appr_date: '',
    suppervisor_appr_id: '',
    suppervisor_appr_date: '',
    status: '0',
    check_in_date: '',
    approved_by: '',
    rejected_date: '',
    rejected_by: '',
    comments: '',
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [leavesResponse, usersResponse, leaveCreditsResponse] = await Promise.all([
          getData('proccess.php', 'leaves_V2'),
          getData('proccess.php', 'staffs'),
          getData('proccess.php', 'leaveCredits_V2')
        ]);

        if (leavesResponse.success) {
          setLeaves(leavesResponse.data);
        } else {
          toast.error(leavesResponse.message || "Failed to load leaves");
        }

        if (usersResponse.success) {
          setStaffs(usersResponse.data);
        } else {
          toast.error(usersResponse.message || "Failed to load staff");
        }

        if (leaveCreditsResponse.success) {
          setLeaveCredits(leaveCreditsResponse.data);
        } else {
          toast.error(leaveCreditsResponse.message || "Failed to load leave credits");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // ─── Export to CSV ──────────────────────────────────────────────────────────

  const exportToExcel = () => {
    if (leaves.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const statusMap = {
        0: 'Pending',
        1: 'Inprogress',
        2: 'Approved',
        3: 'Rejected',
        4: 'Returned'
      };

      const exportData = leaves.map(leave => {
        const staff              = staffs.find(s => s.Id === leave.staff_id);
        const hrApprover         = staffs.find(s => s.Id === leave.hr_appr_id);
        const supervisorApprover = staffs.find(s => s.Id === leave.supervisor_appr_id);
        const approvedBy         = staffs.find(s => s.Id === leave.approved_by);
        const rejectedBy         = staffs.find(s => s.Id === leave.rejected_by);

        return {
          'ID':                      leave.id || '',
          'Staff Name':              staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown',
          'Leave Type':              leave.leave_type || '',
          'Reason':                  leave.reason || '',
          'Start Date':              leave.startDate || '',
          'End Date':                leave.endDate || '',
          'Back In Office':          leave.back_in_office || '',
          'Department':              leave.department || '',
          'HR Approver':             hrApprover ? `${hrApprover.FirstName} ${hrApprover.LastName}` : '',
          'HR Approval Date':        leave.hr_appr_date || '',
          'Supervisor Approver':     supervisorApprover ? `${supervisorApprover.FirstName} ${supervisorApprover.LastName}` : '',
          'Supervisor Approval Date':leave.supervisor_appr_date || '',
          'Status':                  statusMap[leave.status] || 'Unknown',
          'Check In Date':           leave.check_in_date || '',
          'Approved By':             approvedBy ? `${approvedBy.FirstName} ${approvedBy.LastName}` : 'N/A',
          'Rejected Date':           leave.rejected_date || '',
          'Rejected By':             rejectedBy ? `${rejectedBy.FirstName} ${rejectedBy.LastName}` : 'N/A',
          'Comments':                leave.comments || ''
        };
      });

      const headers    = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url  = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `leaves_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const findStaff = (id) => {
    const staff = staffs.find(s => s.Id === id);
    if (!staff) {
      return `Staff member not found for`;
    }
    return [staff];
  };

  // ─── Dialog handlers ────────────────────────────────────────────────────────

  const handleAddClick = () => {
    setFormData({
      staff_id: '',
      leave_type: '',
      reason: '',
      startDate: '',
      endDate: '',
      back_in_office: '',
      department: '',
      hr_appr_id: '',
      hr_appr_date: '',
      supervisor_appr_id: '',
      supervisor_appr_date: '',
      status: '0',
      check_in_date: '',
      approved_by: '',
      rejected_date: '',
      rejected_by: '',
      comments: '',
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    setFormData({
      staff_id:       leave.staff_id       || '',
      leave_type:     leave.leave_type     || '',
      reason:         leave.reason         || '',
      startDate:      leave.startDate      || '',
      endDate:        leave.endDate        || '',
      back_in_office: leave.back_in_office || '',
      department:     leave.department     || '',
      comments:       leave.comments       || '',
    });
    setOpenEditDialog(true);
  };

  // ─── Approval / rejection handlers ─────────────────────────────────────────

  const handleHrApproveClick = async (leave) => {
    let staff = findStaff(leave.staff_id);
    if (typeof staff === 'string') {
      toast.error(staff);
      return;
    }

    const creditRes = await getData('proccess.php', 'leaveCredits_V2');
    
    const credit    = creditRes.success && creditRes.data?.find(c => c.user_id === leave.staff_id && c.leave_type === leave.leave_type);
    if (!credit || (credit.leftCredit ?? 0) <= 0) {
      toast.error("Cannot approve: Staff member has no remaining leave credits");
      return;
    }

    try {
      const response = await updateData('proccess.php', 'leaves_V2', {
        updType:      'hr',
        status:       1,
        hr_appr_id:   userID,
        hr_appr_date: new Date().toISOString().split('T')[0]
      }, leave.id);

      if (response.success) {
        toast.success("Leave approved successfully");
        setLeaves(response.data.map(l => ({ ...l, id: l.id })));
      } else {
        toast.error(response.message || "Failed to approve leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve leave");
      console.error("Approve error:", err);
    }
  };

  const handleSupervisorApproveClick = async (leave) => {
    // Find the leave credits record for this staff member and leave type
    const creditRecord = leaveCredits.find(c => c.user_id === leave.staff_id && c.leave_type === leave.leave_type);
    
    // Validate credit record exists
    if (!creditRecord) {
      toast.error("No leave credit record found for this staff member and leave type");
      return;
    }

    // Check if staff has remaining leave credits (using leftCredit directly)
    if (creditRecord.leftCredit <= 0) {
      toast.error("Cannot approve leave: Staff member has no remaining leave credits");
      return;
    }

    // Calculate working days (excluding weekends and public holidays)
    const duration = countWorkingDays(leave.startDate, leave.endDate);
    
    // Check if requested days exceed available credits
    if (duration > creditRecord.leftCredit) {
      toast.error(`Cannot approve: Requested ${duration} working day(s) but only ${creditRecord.leftCredit} credit(s) remaining`);
      return;
    }

    try {
      // Update leave status to "Approved" (status 2)
      const response = await updateData('proccess.php', 'leaves_V2', {
        updType:              'spvsr',
        status:               2,
        supervisor_appr_id:   userID,
        supervisor_appr_date: new Date().toISOString().split('T')[0]
      }, leave.id);

      if (!response.success) {
        toast.error(response.message || "Failed to approve leave");
        return;
      }

      toast.success("Leave approved successfully");
      setLeaves(response.data.map(l => ({ ...l, id: l.id })));

      // Update leave credits (deduct the working days used)
      const newUsedUp = creditRecord.usedUpCredit + duration;
      const newLeft = creditRecord.leaveCredit - newUsedUp;

      const creditResponse = await updateData('proccess.php', 'leaveCredits_V2', {
        usedUpCredit: newUsedUp,
        leftCredit: newLeft,
      }, creditRecord.id); // Use the credit record's ID, not staff_id

      if (creditResponse.success) {
        // Update local leaveCredits state
        setLeaveCredits(prev => prev.map(c =>
          c.id === creditRecord.id
            ? { ...c, usedUpCredit: newUsedUp, leftCredit: newLeft }
            : c
        ));
      } else {
        toast.error(creditResponse.message || "Failed to update leave credits");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve leave");
      console.error("Approve error:", err);
    }
  };

  const handleApproveClick = async (leave) => {
    const now              = Date.now();
    const backInOfficeTime = new Date(leave.back_in_office).getTime();
    const isToday          = new Date(leave.back_in_office).toDateString() === new Date().toDateString();
    const isLate           = backInOfficeTime < now && !isToday;
    const isStillOut       = backInOfficeTime > now;

    if (isStillOut) {
      toast.error("The staff is still on leave!");
      return;
    }

    const daysLate = Math.floor((now - backInOfficeTime) / (1000 * 60 * 60 * 24));
    const comments = isLate
      ? `Staff returned late by ${daysLate} day(s).`
      : 'Welcome back.';

    try {
      const response = await updateData('proccess.php', 'leaves_V2', {
        updType:        'appr',
        status:         4,
        comments,
        approved_by:    userID,
        check_in_date:  new Date().toISOString().split('T')[0]
      }, leave.id);

      if (response.success) {
        toast.success("Leave approved successfully");
        setLeaves(response.data.map(l => ({ ...l, id: l.id })));
      } else {
        toast.error(response.message || "Failed to approve leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve leave");
      console.error("Approve error:", err);
    }
  };

  const handleRejectClick = async (leave) => {
    try {
      const response = await updateData('proccess.php', 'leaves_V2', {
        updType:       'reject',
        status:        3,
        rejected_by:   userID,
        rejected_date: new Date().toISOString().split('T')[0]
      }, leave.id);

      if (response.success) {
        toast.success("Leave rejected successfully");
        setLeaves(response.data.map(l => ({ ...l, id: l.id })));
      } else {
        toast.error(response.message || "Failed to reject leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to reject leave");
      console.error("Reject error:", err);
    }
  };

  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData('proccess.php', 'leaves_V2', leaveToDelete.id);
      if (response.success) {
        toast.success("Leave deleted successfully");
        setLeaves(leaves.filter(l => l.id !== leaveToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete leave");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setLeaveToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ─── Submit with leave-duration validation ──────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate leave duration against type limits
    const limit = LEAVE_TYPE_LIMITS[formData.leave_type];
    if (limit !== undefined && formData.startDate && formData.endDate) {
      const days = countWorkingDays(formData.startDate, formData.endDate);
      if (days > limit) {
        toast.error(
          `${formData.leave_type} leave cannot exceed ${limit} working day${limit === 1 ? '' : 's'}. ` +
          `You selected ${days} working day${days === 1 ? '' : 's'}.`
        );
        return;
      }
    }

    try {
      let response;
      if (selectedLeave) {
        response = await updateData('proccess.php', 'leaves_V2', formData, selectedLeave.id);
        if (response.success) {
          toast.success("Leave updated successfully");
          setLeaves(response.data.map(l => ({ ...l, id: l.id })));
        }
      } else {
        response = await addData('proccess.php', 'leaves_V2', formData);
        if (response.success) {
          toast.success("Leave added successfully");
          setLeaves(response.data.map(l => ({ ...l, id: l.id })));
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }

    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedLeave(null);
  };

  // ─── DataGrid columns ───────────────────────────────────────────────────────

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 50 },
    {
      field: "staff_id",
      headerName: "Full Name",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.staff_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown'}
          </Box>
        );
      }
    },
    { field: "leave_type", headerName: "Leave Type", flex: 1, minWidth: 120 },
    { field: "reason",     headerName: "Reason",     flex: 1, minWidth: 120 },
    { field: "startDate",  headerName: "Start Date", flex: 1, minWidth: 120 },
    { field: "endDate",    headerName: "End Date",   flex: 1, minWidth: 120 },
    {
      field: "leavedays",
      headerName: "Leave Days",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const leavedays = countWorkingDays(
          params.row.startDate,
          params.row.endDate
        );
        return (
          <Box width="100%">
            <Typography variant="h5" fontWeight={700} color={colors.red[500]} sx={{ paddingTop: '1.1rem' }}>
              {leavedays <= 1 ? `${leavedays} Day` : `${leavedays} Days`}
            </Typography>
          </Box>
        );
      }
    },
    { field: "back_in_office", headerName: "Back In Office", flex: 1, minWidth: 120 },
    { field: "department",     headerName: "Department",     flex: 1, minWidth: 120 },
    {
      field: "leaveCredit",
      headerName: "Leave Credits",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const leaveCredit = leaveCredits.find(l => l.user_id === params.row?.staff_id)?.leaveCredit || 0;
        return <Box width="100%">{leaveCredit}</Box>;
      }
    },
    {
      field: "usedUpCredit",
      headerName: "Used Credits",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const usedUpCredit = leaveCredits.find(l => l.user_id === params.row?.staff_id)?.usedUpCredit || 0;
        return <Box width="100%">{usedUpCredit}</Box>;
      }
    },
    {
      field: "leftCredit",
      headerName: "Left Credits",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const leftCredit = leaveCredits.find(l => l.user_id === params.row?.staff_id)?.leftCredit || 0;
        return <Box width="100%">{leftCredit}</Box>;
      }
    },
    {
      field: "hr_appr_id",
      headerName: "Supervisor Approval",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.hr_appr_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : ''}
            {(userRole === 'supervisor' || userRole === 'manager' || userRole === 'ceo' || userRole === 'coo' || userRole === 'supperAdmin') && params.row?.status === 0 &&
              <IconButton
                onClick={() => handleHrApproveClick(params.row)}
                sx={{ color: colors.blue[400], backgroundColor: colors.indigo[400] }}
                disabled={params.row?.status !== 0}
              >
                <CheckOutlinedIcon />
              </IconButton>
            }
          </Box>
        );
      }
    },
    { field: "hr_appr_date", headerName: "Approval Date", flex: 1, minWidth: 120 },
    {
      field: "supervisor_appr_id",
      headerName: "Management Approval",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.supervisor_appr_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : ''}
            {(userRole === 'ceo' || userRole === 'coo' || userRole === 'supperAdmin') && params.row?.status === 1 &&
              <IconButton
                onClick={() => handleSupervisorApproveClick(params.row)}
                sx={{ color: colors.blue[400], backgroundColor: colors.indigo[400] }}
                disabled={params.row?.status !== 1}
              >
                <CheckOutlinedIcon />
              </IconButton>
            }
          </Box>
        );
      }
    },
    { field: "supervisor_appr_date", headerName: "Approval Date", flex: 1, minWidth: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const statusText = {
          0: 'Pending',
          1: 'Inprogress',
          2: 'Approved',
          3: 'Rejected',
          4: 'Returned'
        };
        const statusColor = {
          0: colors.orange[100],
          1: colors.orange[100],
          2: colors.blue[100],
          3: colors.red[100],
          4: colors.blue[100],
        };
        return (
          <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h5" fontWeight="bold" color={statusColor[params.row?.status]}>
              {statusText[params.row?.status] || 'Unknown'}
            </Typography>
          </Box>
        );
      }
    },
    { field: "check_in_date", headerName: "Check In Date", flex: 1, minWidth: 120 },
    {
      field: "approved_by",
      headerName: "Approved By",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.approved_by);
        return staff ? `${staff.FirstName} ${staff.LastName}` : 'N/A';
      }
    },
    { field: "rejected_date", headerName: "Rejected Date", flex: 1, minWidth: 120 },
    {
      field: "rejected_by",
      headerName: "Rejected By",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.rejected_by);
        return staff ? `${staff.FirstName} ${staff.LastName}` : 'N/A';
      }
    },
    { field: "comments", headerName: "Comment", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleApproveClick(params.row)}
            sx={{ color: colors.blue[400], backgroundColor: colors.indigo[400] }}
            disabled={(params.row?.status === 4 || params.row?.status < 2) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor' || userRole === 'supperAdmin' || userRole === 'manager' || userRole === 'ceo')}
          >
            <CheckOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleRejectClick(params.row)}
            sx={{ color: colors.red[400] }}
            disabled={(params.row?.status === 1 || params.row?.status > 1) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor' || userRole === 'supperAdmin' || userRole === 'manager' || userRole === 'ceo')}
          >
            <ClearOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.red[800] }}
            disabled={(params.row?.status === 1 || params.row?.status > 1) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor' || userRole === 'supperAdmin' || userRole === 'manager' || userRole === 'ceo')}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // ─── No-data overlay ────────────────────────────────────────────────────────

  const NoDataOverlay = () => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" color={colors.dark[400]}>
      <img src={Nodata} alt="No data" style={{ width: 150, marginBottom: 16 }} />
      <Typography variant="h6">No data available</Typography>
    </Box>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box m="10px">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add / Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedLeave(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedLeave ? "Edit Leave" : "Add New Leave"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display="flex" gap="1rem" width="100%" mb="1rem" flexWrap="wrap">

              <TextField
                select
                style={{ width: "100%" }}
                id="staff_id"
                name="staff_id"
                label="Staff Member"
                variant="standard"
                value={formData.staff_id}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {staffs.map((staff) => (
                  <option key={staff.Id} value={staff.Id}>
                    {staff.FirstName} {staff.LastName}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                style={{ width: "100%" }}
                id="leave_type"
                name="leave_type"
                label="Leave Type"
                variant="standard"
                value={formData.leave_type}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
                helperText={
                  formData.leave_type && LEAVE_TYPE_LIMITS[formData.leave_type] !== undefined
                    ? `Maximum: ${LEAVE_TYPE_LIMITS[formData.leave_type]} working day${LEAVE_TYPE_LIMITS[formData.leave_type] === 1 ? '' : 's'}`
                    : ''
                }
              >
                <option value=""></option>
                <option value="Annual">Annual</option>
                <option value="Sick">Sick</option>
                <option value="Compassionate">Compassionate</option>
                <option value="Study">Study</option>
                <option value="Maternity">Maternity</option>
              </TextField>

              <TextField
                style={{ width: "100%" }}
                id="reason"
                name="reason"
                label="Reason"
                variant="standard"
                value={formData.reason}
                onChange={handleFormChange}
                required
                multiline
                rows={2}
              />

              <TextField
                style={{ width: "48%" }}
                id="startDate"
                name="startDate"
                label="Start Date"
                type="date"
                variant="standard"
                value={formData.startDate}
                onChange={handleFormChange}
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                style={{ width: "48%" }}
                id="endDate"
                name="endDate"
                label="End Date"
                type="date"
                variant="standard"
                value={formData.endDate}
                onChange={handleFormChange}
                required
                InputLabelProps={{ shrink: true }}
                helperText={
                  formData.startDate && formData.endDate
                    ? (() => {
                        const days = countWorkingDays(formData.startDate, formData.endDate);
                        const limit = LEAVE_TYPE_LIMITS[formData.leave_type];
                        const over  = limit !== undefined && days > limit;
                        return (
                          <span style={{ color: over ? 'red' : 'inherit' }}>
                            {days} working day{days === 1 ? '' : 's'} selected
                            {over ? ` — exceeds ${limit}-day limit` : ''}
                          </span>
                        );
                      })()
                    : ''
                }
              />

              <TextField
                style={{ width: "48%" }}
                id="back_in_office"
                name="back_in_office"
                label="Back In Office"
                type="date"
                variant="standard"
                value={formData.back_in_office}
                onChange={handleFormChange}
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                style={{ width: "48%" }}
                id="department"
                name="department"
                label="Department"
                variant="standard"
                value={formData.department}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                <option value="ICT">ICT</option>
                <option value="SCIENCE">SCIENCE</option>
                <option value="ART">ART</option>
                <option value="HR">HR</option>
                <option value="ADMIN">ADMIN</option>
                <option value="STEAM">STEAM</option>
                <option value="ENGINEERING">ENGINEERING</option>
                <option value="HYGIENE">HYGIENE</option>
              </TextField>

              {selectedLeave && (
                <TextField
                  style={{ width: "100%" }}
                  id="comments"
                  name="comments"
                  label="Comments"
                  variant="standard"
                  value={formData.comments}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                />
              )}
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setOpenAddDialog(false);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedLeave ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this leave request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Box mb="30px" ml="15px" mr="15px" display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Typography variant="h4" color={colors.dark[400]} fontWeight="bold" sx={{ mb: "5px" }}>
            Staff Leaves Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              borderColor: colors.green[400],
              color: colors.green[400],
              '&:hover': {
                backgroundColor: colors.green[50],
                borderColor: colors.green[500],
              }
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': { backgroundColor: colors.blue[500] }
            }}
          >
            Add Leave Request
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* DataGrid */}
      <Box sx={{ height: 'calc(100vh - 200px)', width: "100%" }}>
        <DataGrid
          rows={leaves || []}
          columns={columns}
          getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
          loading={loading}
          getRowClassName={(params) => {
            switch (params.row.status) {
              case 0: return 'status-pending';
              case 1: return 'status-approved';
              case 2: return 'status-supervisor-approved';
              case 3: return 'status-rejected';
              case 4: return 'status-returned';
              default: return '';
            }
          }}
          sx={{
            border: "none",
            backgroundColor: colors.primary[500],
            ".MuiDataGrid-cell": { borderBottom: "none" },
            "& .status-pending .MuiDataGrid-cell": {
              backgroundColor: '#af928257',
              color: colors.orange[200],
              borderBottom: `.2px solid ${colors.orange[400]}`,
            },
            "& .status-approved .MuiDataGrid-cell": {
              backgroundColor: '#6f968459',
              color: colors.newTeal[200],
              borderBottom: `.2px solid ${colors.green[400]}`,
            },
            "& .status-supervisor-approved .MuiDataGrid-cell": {
              backgroundColor: '#60a3d356',
              color: colors.indigo[200],
              borderBottom: `.2px solid ${colors.blue[400]}`,
            },
            "& .status-rejected .MuiDataGrid-cell": {
              backgroundColor: '#cf04223a',
              color: colors.red[100],
              borderBottom: `.2px solid ${colors.red[400]}`,
            },
            "& .status-returned .MuiDataGrid-cell": {
              borderBottom: `.2px solid ${colors.indigo[400]}`,
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: 'transparent',
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Leave;